/**
 * Application Routes Configuration
 * Centralized routing with lazy loading for code splitting
 */

import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import LoadingSpinner from '../shared/components/LoadingSpinner';
import ErrorBoundary from '../shared/components/ErrorBoundary';
import { useAuth } from '../features/auth/contexts/AuthContext';

// Layouts
import AuthLayout from '../shared/layouts/AuthLayout';
import MainLayout from '../shared/layouts/MainLayout';

// Lazy load pages for code splitting
const LoginPage = lazy(() => import('../features/auth/components/LoginPage'));
const ForgotPasswordPage = lazy(() => import('../features/auth/components/ForgotPasswordPage'));
const ChangePasswordPage = lazy(() => import('../features/auth/components/ChangePasswordPage'));

const DashboardPage = lazy(() => import('../features/dashboard/DashboardPage'));
const DemandsPage = lazy(() => import('../features/demands/DemandsPage'));
const CandidatePipelinePage = lazy(() => import('../features/demands/CandidatePipelinePage'));
const AssociatesPage = lazy(() => import('../features/associates/AssociatesPage'));
const RolesPage = lazy(() => import('../features/roles/RolesPage'));
const EmailTemplatesPage = lazy(() => import('../features/email-templates/EmailTemplatesPage'));
const SettingsPage = lazy(() => import('../features/settings/SettingsPage'));
const ProfileSearchPage = lazy(() => import('../features/profile-search/ProfileSearchPage'));
const CustomReportPage = lazy(() => import('../features/reports/CustomReportPage'));
const LoginActivityPage = lazy(() => import('../features/login-activity/LoginActivityPage'));
const DemandReportPage = lazy(() => import('../features/demand-report/DemandReportPage'));

/**
 * Protected Route Component
 * Redirects to login if not authenticated
 */
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

/**
 * Public Route Component
 * Redirects to dashboard if already authenticated
 */
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

/**
 * App Routes
 */
const AppRoutes = () => {
  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingSpinner fullScreen />}>
        <Routes>
          {/* Public Routes */}
          <Route element={<AuthLayout />}>
            <Route
              path="/"
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <PublicRoute>
                  <ForgotPasswordPage />
                </PublicRoute>
              }
            />
          </Route>

          {/* Protected Routes */}
          <Route
            element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/demands" element={<DemandsPage />} />
            <Route path="/candidate-pipeline" element={<CandidatePipelinePage />} />
            <Route path="/associates" element={<AssociatesPage />} />
            <Route path="/profile-search" element={<ProfileSearchPage />} />
            <Route path="/roles" element={<RolesPage />} />
            <Route path="/email-templates" element={<EmailTemplatesPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/reports" element={<CustomReportPage />} />
            <Route path="/login-activity" element={<LoginActivityPage />} />
            <Route path="/demand-report" element={<DemandReportPage />} />
            <Route path="/change-password" element={<ChangePasswordPage />} />
          </Route>

          {/* 404 - Not Found */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
};

export default AppRoutes;

